import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { placesAutocomplete, getPlaceDetails, type PlaceDetail, type PlaceSuggestion } from '../../api/endpoints/places';
import styles from './AddressAutocompleteField.module.css';

interface Props {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (detail: PlaceDetail) => void;
  onCleared: () => void;
  required?: boolean;
  /** Pin colour: green for a start point, red (default) for an end point or checkpoint. */
  pinTone?: 'origin' | 'destination';
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
    </svg>
  );
}

/**
 * Address input with debounced Google Places autocomplete (via the backend
 * proxy at /places/**, same one rhino_core's AddressAutocompleteField uses).
 * Session-tokens the autocomplete+details round trip for Places billing and
 * gracefully degrades to a plain text field if the API call fails.
 */
export function AddressAutocompleteField({
  label,
  placeholder = 'Search address or place',
  value,
  onChange,
  onPlaceSelected,
  onCleared,
  required,
  pinTone = 'destination',
}: Props) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listId = `${baseId}-list`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;
  const listOpen = open && suggestions.length > 0;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const ensureSessionToken = () => (sessionTokenRef.current ??= crypto.randomUUID());

  const handleChange = (text: string) => {
    onChange(text);
    if (hasSelection) {
      setHasSelection(false);
      onCleared();
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (text.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const token = ensureSessionToken();
      setLoading(true);
      try {
        const results = await placesAutocomplete(text, token);
        setSuggestions(results);
        setHighlight(-1);
        setOpen(results.length > 0);
      } catch {
        // Graceful degradation: API failure falls back to plain text.
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleSelect = async (suggestion: PlaceSuggestion) => {
    setOpen(false);
    const token = sessionTokenRef.current;
    sessionTokenRef.current = null;
    onChange(suggestion.fullText || suggestion.primaryText);
    setLoading(true);
    try {
      const detail = await getPlaceDetails(suggestion.placeId, token ?? undefined);
      setHasSelection(true);
      onPlaceSelected(detail);
    } catch {
      // Fallback: if details fail, text stays filled with the suggestion text.
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const n = suggestions.length;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (n === 0) return;
      e.preventDefault();
      if (!listOpen) setOpen(true);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (h < 0 ? (step === 1 ? 0 : n - 1) : (h + step + n) % n));
    } else if (e.key === 'Enter') {
      if (listOpen && highlight >= 0) {
        e.preventDefault();
        void handleSelect(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      if (listOpen) {
        e.stopPropagation();
        setOpen(false);
      }
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef} data-pin-tone={pinTone}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>
      <div className={styles.inputRow}>
        <PinIcon className={styles.pin} />
        <input
          id={inputId}
          className={styles.input}
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={listOpen && highlight >= 0 ? optionId(highlight) : undefined}
          value={value}
          placeholder={placeholder}
          required={required}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
        />
        <span className={styles.status}>
          {loading ? (
            <span className={styles.spinner} aria-hidden />
          ) : hasSelection ? (
            <span className={styles.checkmark} aria-hidden>
              ✓
            </span>
          ) : null}
        </span>
      </div>
      {listOpen && (
        <div className={styles.dropdown}>
          <div id={listId} role="listbox" aria-label={label} className={styles.list}>
            {suggestions.map((s, i) => (
              <div
                key={s.placeId}
                id={optionId(i)}
                role="option"
                aria-selected={i === highlight}
                className={`${styles.option} ${i === highlight ? styles.optionActive : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => handleSelect(s)}
              >
                <PinIcon className={styles.optionPin} />
                <span className={styles.optionText}>
                  <span className={styles.optionPrimary}>{s.primaryText}</span>
                  {s.secondaryText && <span className={styles.optionSecondary}>{s.secondaryText}</span>}
                </span>
              </div>
            ))}
          </div>
          <div className={styles.footer}>{t('domain.addressAutocompleteField.poweredBy')}</div>
        </div>
      )}
    </div>
  );
}
